import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from "@angular/core";
import {
  RecommendationFeedback,
  RecommendationModel,
  RecommendationSeverity,
  RecommendationStatus,
  RecommendationType,
  InsightEntityType,
  InsightVariableTag
} from "../../../models/recommendation.model";
import {Direction} from "@angular/cdk/bidi";

require('./recommendations-list-item.scss');
@Component({
  standalone: false,
  selector: 'app-recommendations-list-item',
  templateUrl: './recommendations-list-item.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecommendationsListItemComponent implements OnChanges {
  @Input() showRecommendationLink: boolean = true;
  @Input() recommendation: RecommendationModel;
  @Input() direction: Direction = 'ltr';
  @Output() updateRecommendation = new EventEmitter<RecommendationModel>();
  @Output() goToRecommendation = new EventEmitter<RecommendationModel>();
  @Output() markRecommendationAsRead = new EventEmitter<RecommendationModel>();
  @Output() applyRecommendation = new EventEmitter<RecommendationModel>();
  @Output() openRoutePanelsByIds = new EventEmitter<string[]>();
  RecommendationSeverity = RecommendationSeverity;
  RecommendationFeedback = RecommendationFeedback;

  public parametersData: { [key: string]: string } | null = null;
  public routeIds: string | null = null;

  constructor(@Inject('analyticsService') private analyticsService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.recommendation) {
      this.parametersData = this._getParametersData();
      this.routeIds = this._getRouteIds();
    }
  }

  private _getParametersData(): { [key: string]: string } | null {
    const entities = this.recommendation?.insightOutput?.insightEntities;
    if (!entities || entities.length === 0) {
      return null;
    }

    const parametersData: { [key: string]: string } = {};
    entities.forEach(entity => {
      if (entity.tag && entity.displayName) {
        parametersData[entity.tag] = entity.displayName;
      }
    });

    return Object.keys(parametersData).length > 0 ? parametersData : null;
  }

  private _getRouteIds(): string | null {
    if (this.recommendation?.recommendationType !== RecommendationType.CustomizedInsight) {
      return null;
    }

    const entities = this.recommendation?.insightOutput?.insightEntities;
    if (!entities) {
      return null;
    }

    const routeEntity = entities.find(entity => entity.insightEntityType === InsightEntityType.Route && entity.tag === InsightVariableTag.RouteIds);
    return routeEntity?.displayName || null;
  }

  updateRecommendationFeedback(feedback: RecommendationFeedback) {
    const action = feedback === RecommendationFeedback.POSITIVE
      ? 'recommendation_feedback_given_positive'
      : 'recommendation_feedback_given_negative';
    this.analyticsService.trackEvent('Recommendations Actions', action, this.recommendation.id);

    this.updateRecommendation.emit({
      ...this.recommendation,
      feedback
    });
  }

  markRecommendationToRead() {
    this.analyticsService.trackEvent('Recommendations Actions', 'recommendation_marked_read', this.recommendation.id);

    this.markRecommendationAsRead.emit({
      ...this.recommendation,
      status: RecommendationStatus.Expired
    })
  }

  onApplyRecommendation() {
    this.analyticsService.trackEvent('Recommendations Actions', 'recommendation_applied', this.recommendation.id);
    this.applyRecommendation.emit(this.recommendation);
  }

  onGoToRecommendation() {
    this.analyticsService.trackEvent('Recommendations Actions', 'recommendation_action_clicked', this.recommendation.id);
    this.goToRecommendation.emit(this.recommendation);
  }

  onClickOnRoutes(routeIdsString: string) {
    const routeIds = routeIdsString.split(',').map(routeId => routeId.trim()).filter(routeId => !!routeId);
    this.openRoutePanelsByIds.emit(routeIds);
  }

}
